import type { ImagePickerAsset } from 'expo-image-picker';
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type DocumentSnapshot,
  type FirestoreError,
  type Query,
  type QuerySnapshot,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref,
  uploadBytes,
  uploadBytesResumable,
  type UploadMetadata,
  type UploadResult,
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

import { db, storage } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';
import {
  blobFromMediaAsset,
  MAX_PHOTO_UPLOAD_SOURCE_BYTES,
  prepareImageUpload,
} from '@/src/features/media/image-upload';
import {
  getProfileAvatarURL,
  getPublicProfile,
} from '@/src/features/profile/profile-service';
import type { GiphyGif } from '@/src/features/giphy/giphy-service';
import { fetchPublicJson } from '@/src/utils/public-api';
import { isPermissionDeniedError, UserFacingError } from '@/src/utils/errors';

import { profileMentionsForText } from './profile-mention-helpers';

export const PUBLIC_POST_LIMIT = 25;
const FOLLOWING_POST_QUERY_CHUNK_SIZE = 30;
export const FEED_RANKING_VERSION = 3;
export const RANKED_FEED_CLIENT_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
export const MAX_POST_BODY_LENGTH = 2000;
export const MAX_COMMENT_BODY_LENGTH = 1000;
export const MAX_POST_IMAGE_BYTES = MAX_PHOTO_UPLOAD_SOURCE_BYTES;
export const MAX_POST_VIDEO_BYTES = 1024 * 1024 * 1024;
export const MAX_POST_PHOTO_ITEMS = 10;
export const MAX_VIDEO_DURATION_SECONDS = 10 * 60;
export const MAX_VIDEO_DURATION_MILLIS = MAX_VIDEO_DURATION_SECONDS * 1000;
const MIN_FEED_IMAGE_ASPECT_RATIO = 4 / 5;
const MAX_FEED_IMAGE_ASPECT_RATIO = 21 / 9;

export const POST_VISIBILITIES = ['public', 'friends', 'just_me'] as const;
export type PostVisibility = (typeof POST_VISIBILITIES)[number];
export type PostMediaKind = 'image' | 'video';
export type CommentUploadMediaKind = 'image' | 'video';
export type CommentMediaKind = CommentUploadMediaKind | 'gif';
export type FirestoreDate = Timestamp | Date;
export type MediaThumbnailStatus = 'queued' | 'processing' | 'ready' | 'failed';
export const REACTIONS = [
  'love',
  'like',
  'dislike',
  'laugh',
  'wow',
  'vomit',
  'fire',
  'trash',
] as const;
export const COMMENT_REACTIONS = REACTIONS;

export type Reaction = (typeof REACTIONS)[number];
export const DEFAULT_REACTION = 'love' satisfies Reaction;
export type ReactionCounts = Record<Reaction, number>;
export type CommentReaction = Reaction;
export type CommentReactionCounts = ReactionCounts;
export type PostReaction = Reaction;
export type PostReactionCounts = ReactionCounts;
export type FeedKind = 'home' | 'frienzzone';
export type FeedMode = 'ranked' | 'latest';
export type FeedEventType =
  | 'comment_open'
  | 'detail_open'
  | 'dwell'
  | 'hide'
  | 'impression'
  | 'less_like_this'
  | 'post_engagement';
export type FeedEventUpload = {
  commentOpenCount?: number;
  detailOpenCount?: number;
  dwellBucket?: number;
  dwellBucketTotal?: number;
  feedKind: FeedKind;
  hidden?: boolean;
  impressionCount?: number;
  lessLikeThis?: boolean;
  postEngagementCount?: number;
  postId: string;
  type?: FeedEventType;
};
export type FeedReasonCode =
  | 'active_conversation'
  | 'fresh_unseen'
  | 'garage_build'
  | 'higher_engagement'
  | 'less_like_this_applied'
  | 'mutual_friend'
  | 'photo_or_video'
  | 'popular_with_frienzzi'
  | 'recent_activity'
  | 'recent_post'
  | 'similar_to_posts_you_open'
  | 'you_created_this'
  | 'you_follow_author';
export type RankedFeedSlot =
  | 'active_conversation'
  | 'exploration'
  | 'fresh_unseen'
  | 'high_engagement'
  | 'relationship';

export type ProfileMention = {
  uid: string;
  label: string;
};

export type MediaThumbnail = {
  storagePath: string;
  downloadURL: string;
  contentType: 'image/jpeg';
  sizeBytes: number;
  width: number;
  height: number;
  generatedAt: FirestoreDate | null;
};

export type PostMedia = {
  kind: PostMediaKind;
  storagePath: string;
  downloadURL: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
  durationMillis: number | null;
  thumbnail: MediaThumbnail | null;
  thumbnailStatus: MediaThumbnailStatus | null;
  thumbnailError?: string | null;
};

export type PostMediaItem = PostMedia & {
  id: string;
  order: number;
};

function legacyPostMediaFromItem(item: PostMediaItem) {
  return {
    kind: item.kind,
    storagePath: item.storagePath,
    downloadURL: item.downloadURL,
    contentType: item.contentType,
    sizeBytes: item.sizeBytes,
    width: item.width,
    height: item.height,
    durationMillis: item.durationMillis,
    thumbnail: item.thumbnail,
    thumbnailStatus: item.thumbnailStatus,
  };
}

export type LinkPreviewStatus = 'ready' | 'failed';

export type LinkPreview = {
  status: LinkPreviewStatus;
  url: string;
  displayUrl: string;
  title: string | null;
  description: string | null;
  imageURL: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  siteName: string | null;
  fetchedAt: FirestoreDate | string | null;
};

export type PostFeedStats = {
  commentOpenCount: number;
  detailOpenCount: number;
  dwellBucketTotal: number;
  impressionCount: number;
};

export type PublicPost = {
  id: string;
  authorUid: string;
  authorFullName: string | null;
  authorUsername: string | null;
  authorPhotoURL: string | null;
  authorFounderBadge?: boolean;
  authorVerifiedBadge?: boolean;
  body: string | null;
  visibility: PostVisibility;
  media: PostMedia | null;
  mediaItems?: PostMediaItem[];
  linkCard: PostLinkCard | null;
  linkPreview: LinkPreview | null;
  reshare: PostReshare | null;
  feedStats: PostFeedStats;
  likeCount: number;
  reactionCounts: PostReactionCounts;
  reactionCount: number;
  viewerReaction?: PostReaction | null;
  commentCount: number;
  shareCount: number;
  profileMentions: ProfileMention[];
  createdAt: FirestoreDate | null;
  editCount: number;
  editedAt: FirestoreDate | null;
  updatedAt: FirestoreDate | null;
  ranking?: {
    feedKind: FeedKind;
    position: number;
    rankingVersion: number;
    reasonCodes: FeedReasonCode[];
    score: number;
    scoredAt: FirestoreDate | null;
    slot: RankedFeedSlot;
  };
  localCreatedAtMillis?: number;
  syncStatus?: 'pending' | 'uploading' | 'synced' | 'failed';
};

export type PostReshare = {
  authorUid: string;
  postId: string;
};

export type PostLinkCard = {
  kind: 'vehicle';
  imageURL: string | null;
  subtitle: string | null;
  title: string;
  urlPath: string;
  vehicleId: string;
};

export type CreatePostInput = {
  authorUid: string;
  authorFullName: string | null;
  body: string;
  debugDiagnostics?: boolean;
  linkPreviewSourceUrl?: string | null;
  profileMentions?: ProfileMention[];
  visibility?: PostVisibility;
  mediaAsset?: ImagePickerAsset | null;
  mediaAssets?: ImagePickerAsset[];
  mediaKind?: PostMediaKind | null;
  onUploadProgress?: (progress: number) => void;
  preparedMediaUpload?: PreparedPostMediaUpload | null;
  preparedMediaUploads?: PreparedPostMediaUpload[];
  reshareOfPost?: Pick<PublicPost, 'authorUid' | 'id'> | null;
};

export type PreparedPostMediaUpload = {
  blob: Blob;
  contentType: string;
  durationMillis: number | null;
  fileName: string;
  height: number;
  kind: PostMediaKind;
  sizeBytes: number;
  width: number;
};

export type MentionCandidate = {
  uid: string;
  fullName: string;
  username: string | null;
  photoURL: string | null;
  founderBadge?: boolean;
  verifiedBadge?: boolean;
};

export type PostReactionRecord = {
  uid: string;
  postId: string;
  reaction: PostReaction;
  createdAt: FirestoreDate | null;
  updatedAt: FirestoreDate | null;
};

export type ReactionActor = {
  uid: string;
  reaction: Reaction;
  fullName: string;
  photoURL: string | null;
  createdAt: FirestoreDate | null;
  updatedAt: FirestoreDate | null;
};

const deletePostCallable = httpsCallable<{ postId: string }, { ok: true }>(
  functions,
  'deletePost',
);
const recordPostShareCallable = httpsCallable<
  { postId: string },
  { ok: true; shareCount: number }
>(functions, 'recordPostShare');
const setPostReactionCallable = httpsCallable<
  { mutationId?: string; postId: string; reaction: PostReaction | null },
  { ok: true }
>(functions, 'setPostReaction');
const setPostCommentReactionCallable = httpsCallable<
  {
    commentId: string;
    mutationId?: string;
    postId: string;
    reaction: CommentReaction | null;
  },
  { ok: true }
>(functions, 'setPostCommentReaction');
const createPostCallable = httpsCallable<
  {
    body: string;
    linkPreviewSourceUrl?: string | null;
    media: PostMedia | null;
    mediaItems?: PostMediaItem[] | null;
    postId: string;
    profileMentions: ProfileMention[];
    reshare: PostReshare | null;
    visibility: PostVisibility;
  },
  { ok: true; postId: string }
>(functions, 'createPost');
const editPostCallable = httpsCallable<
  { body: string; postId: string; profileMentions: ProfileMention[] },
  { editCount: number; ok: true }
>(functions, 'editPost');
const previewLinkMetadataCallable = httpsCallable<
  { url: string },
  { preview: LinkPreview | null }
>(functions, 'previewLinkMetadata');
const recordFeedEventsCallable = httpsCallable<
  {
    events: FeedEventUpload[];
  },
  { ok: true }
>(functions, 'recordFeedEvents');
const refreshMyRankedFeedsCallable = httpsCallable<
  { feedKind?: FeedKind },
  {
    count?: number;
    feedKind?: FeedKind;
    frienzzoneCount?: number;
    homeCount?: number;
    ok: true;
  }
>(functions, 'refreshMyRankedFeeds');
const getFeedPostsCallable = httpsCallable<
  { feedKind: FeedKind; limitCount?: number },
  {
    followingCount?: number;
    ok: true;
    posts: (Record<string, unknown> & { id?: unknown })[];
  }
>(functions, 'getFeedPosts');
const getPostCommentsCallable = httpsCallable<
  { limitCount?: number; postId: string },
  {
    comments: (Record<string, unknown> & { id?: unknown })[];
    ok: true;
  }
>(functions, 'getPostComments');
const rankedFeedRefreshState = new Map<
  string,
  { inFlight?: Promise<void>; lastStartedAt: number }
>();
const createPostCommentCallable = httpsCallable<
  {
    body: string;
    commentId: string;
    media: CommentMedia | null;
    parentCommentId?: string | null;
    replyToCommentId?: string | null;
    postId: string;
    profileMentions: ProfileMention[];
  },
  { ok: true; commentId: string }
>(functions, 'createPostComment');
const editPostCommentCallable = httpsCallable<
  {
    body: string;
    commentId: string;
    postId: string;
    profileMentions: ProfileMention[];
  },
  { editCount: number; ok: true }
>(functions, 'editPostComment');
const deletePostCommentCallable = httpsCallable<
  { commentId: string; postId: string },
  { ok: true }
>(functions, 'deletePostComment');
const searchMentionProfilesCallable = httpsCallable<
  {
    postId?: string;
    query: string;
    scope: 'post' | 'comment' | 'vehicle_comment';
    vehicleId?: string;
  },
  { profiles: MentionCandidate[] }
>(functions, 'searchMentionProfiles');

export async function previewLinkMetadata(url: string) {
  const response = await previewLinkMetadataCallable({ url });

  return response.data.preview;
}

export type PostComment = {
  id: string;
  aiGenerated?: boolean;
  authorUid: string;
  authorFullName: string | null;
  authorUsername: string | null;
  authorPhotoURL: string | null;
  authorFounderBadge?: boolean;
  authorVerifiedBadge?: boolean;
  body: string | null;
  botGenerated?: boolean;
  media: CommentMedia | null;
  linkPreview: LinkPreview | null;
  parentCommentId: string | null;
  profileMentions: ProfileMention[];
  reactionCounts: CommentReactionCounts;
  reactionCount: number;
  replyToCommentId: string | null;
  replyToFullName: string | null;
  replyToUid: string | null;
  replyToUsername: string | null;
  createdAt: FirestoreDate | null;
  editCount: number;
  editedAt: FirestoreDate | null;
  updatedAt: FirestoreDate | null;
  localCreatedAtMillis?: number;
  syncStatus?: 'pending' | 'uploading' | 'synced' | 'failed';
};

export type CreatePostCommentInput = {
  authorUid: string;
  body: string;
  giphyGif?: GiphyGif | null;
  mediaAsset?: ImagePickerAsset | null;
  mediaKind?: CommentUploadMediaKind | null;
  onUploadProgress?: (progress: number) => void;
  parentCommentId?: string | null;
  replyToCommentId?: string | null;
  postId: string;
  profileMentions?: ProfileMention[];
};

export type UploadedCommentMedia = {
  kind: CommentUploadMediaKind;
  source: 'upload';
  storagePath: string;
  downloadURL: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
  durationMillis: number | null;
  thumbnail: MediaThumbnail | null;
  thumbnailStatus: MediaThumbnailStatus | null;
  thumbnailError?: string | null;
};

export type GiphyCommentMedia = {
  kind: 'gif';
  source: 'giphy';
  giphyId: string;
  title: string;
  pageURL: string;
  previewURL: string;
  originalURL: string;
  width: number;
  height: number;
};

export type CommentMedia = UploadedCommentMedia | GiphyCommentMedia;

export type ContentEditHistoryEntry = {
  id: string;
  editNumber: number;
  editedAt: FirestoreDate | null;
  editorUid: string;
  nextBody: string | null;
  nextProfileMentions: ProfileMention[];
  previousBody: string | null;
  previousProfileMentions: ProfileMention[];
};

export function emptyReactionCounts(): ReactionCounts {
  return {
    love: 0,
    like: 0,
    dislike: 0,
    laugh: 0,
    wow: 0,
    vomit: 0,
    fire: 0,
    trash: 0,
  };
}

export function emptyCommentReactionCounts(): CommentReactionCounts {
  return emptyReactionCounts();
}

export function isReaction(value: unknown): value is Reaction {
  return REACTIONS.includes(value as Reaction);
}

export function isCommentReaction(value: unknown): value is CommentReaction {
  return isReaction(value);
}

export function isPostVisibility(value: unknown): value is PostVisibility {
  return POST_VISIBILITIES.includes(value as PostVisibility);
}

export function parsePostVisibility(value: unknown): PostVisibility {
  return isPostVisibility(value) ? value : 'public';
}

export function postVisibilityLabel(visibility: PostVisibility) {
  if (visibility === 'friends') {
    return 'Friends';
  }

  if (visibility === 'just_me') {
    return 'Only me';
  }

  return 'Public';
}

export function parseReactionCounts(value: unknown): ReactionCounts {
  const source =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  const counts = emptyReactionCounts();

  REACTIONS.forEach((reaction) => {
    const count = source[reaction];

    counts[reaction] =
      typeof count === 'number' && Number.isFinite(count) && count > 0
        ? Math.floor(count)
        : 0;
  });

  return counts;
}

function finiteCount(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

function parsePostFeedStats(value: unknown): PostFeedStats {
  const source =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};

  return {
    commentOpenCount: finiteCount(source.commentOpenCount),
    detailOpenCount: finiteCount(source.detailOpenCount),
    dwellBucketTotal: finiteCount(source.dwellBucketTotal),
    impressionCount: finiteCount(source.impressionCount),
  };
}

export function parseCommentReactionCounts(
  value: unknown,
): CommentReactionCounts {
  return parseReactionCounts(value);
}

export function totalReactionCounts(counts: ReactionCounts) {
  return REACTIONS.reduce((total, reaction) => {
    return total + counts[reaction];
  }, 0);
}

export function totalCommentReactionCounts(counts: CommentReactionCounts) {
  return totalReactionCounts(counts);
}

function getContentType(asset: ImagePickerAsset, kind: PostMediaKind) {
  if (asset.mimeType) {
    return asset.mimeType;
  }

  return kind === 'image' ? 'image/jpeg' : 'video/mp4';
}

function getFileName(asset: ImagePickerAsset, kind: PostMediaKind) {
  const fallbackExtension = kind === 'image' ? 'jpg' : 'mp4';
  const fallbackName = `media.${fallbackExtension}`;
  return (asset.fileName ?? fallbackName).replace(/[^a-zA-Z0-9._-]/g, '_');
}

function getPostMediaItemId(index: number) {
  return `photo-${index + 1}`;
}

function getPostMediaItemFileName({
  fileName,
  itemId,
}: {
  fileName: string;
  itemId: string;
}) {
  return `${itemId}-${fileName}`.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function validateMediaSize(sizeBytes: number, kind: PostMediaKind) {
  const maxSize =
    kind === 'image' ? MAX_POST_IMAGE_BYTES : MAX_POST_VIDEO_BYTES;

  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    throw new UserFacingError(
      kind === 'image'
        ? 'We could not read that photo. Please choose a different image.'
        : 'We could not read that video. Please choose it again and retry.',
    );
  }

  if (sizeBytes > maxSize) {
    throw new Error(
      kind === 'image'
        ? 'Photos must be 200MB or smaller after processing.'
        : 'Videos must be 1GB or smaller.',
    );
  }
}

function normalizeAssetDimension(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : 0;
}

function normalizeAssetDurationMillis(asset: ImagePickerAsset) {
  return typeof asset.duration === 'number' &&
    Number.isFinite(asset.duration) &&
    asset.duration >= 0
    ? Math.round(asset.duration)
    : null;
}

function normalizeMediaSizeBytes(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : 0;
}

function buildMediaMetrics(asset: ImagePickerAsset, kind: PostMediaKind) {
  const durationMillis =
    kind === 'video' ? normalizeAssetDurationMillis(asset) : null;

  if (
    kind === 'video' &&
    durationMillis != null &&
    durationMillis > MAX_VIDEO_DURATION_MILLIS
  ) {
    throw new Error('Videos must be 10 minutes or shorter.');
  }

  return {
    width: normalizeAssetDimension(asset.width),
    height: normalizeAssetDimension(asset.height),
    durationMillis,
  };
}

function validateMedia(asset: ImagePickerAsset, kind: PostMediaKind) {
  const contentType = getContentType(asset, kind);
  const sizeBytes = normalizeMediaSizeBytes(asset.fileSize);
  const metrics = buildMediaMetrics(asset, kind);

  if (!contentType.startsWith(`${kind}/`)) {
    throw new Error(`Selected file must be a ${kind}.`);
  }

  if (sizeBytes) {
    validateMediaSize(sizeBytes, kind);
  }

  return { contentType, sizeBytes, ...metrics };
}

async function readOriginalMediaBlob(
  asset: ImagePickerAsset,
  kind: PostMediaKind,
) {
  return blobFromMediaAsset(
    asset,
    kind === 'image'
      ? 'We could not read that photo. Please choose a different image.'
      : 'We could not read that video. Please choose it again and retry.',
  );
}

async function preparePostImageUpload(asset: ImagePickerAsset) {
  return prepareImageUpload({
    asset,
    fallbackFileName: getFileName(asset, 'image'),
    jpegQuality: 0.86,
    maxBytes: MAX_POST_IMAGE_BYTES,
  });
}

export async function preparePostMediaUpload(
  asset: ImagePickerAsset,
  kind: PostMediaKind,
): Promise<PreparedPostMediaUpload> {
  if (kind === 'image') {
    const preparedImage = await preparePostImageUpload(asset);

    validateMediaSize(preparedImage.sizeBytes, kind);

    return {
      blob: preparedImage.blob,
      contentType: preparedImage.contentType,
      durationMillis: null,
      fileName: preparedImage.fileName,
      height: preparedImage.height,
      kind,
      sizeBytes: preparedImage.sizeBytes,
      width: preparedImage.width,
    };
  }

  const mediaMetrics = validateMedia(asset, kind);
  const blob = await readOriginalMediaBlob(asset, kind);
  const sizeBytes =
    mediaMetrics.sizeBytes || normalizeMediaSizeBytes(blob.size);

  validateMediaSize(sizeBytes, kind);

  return {
    blob,
    contentType: mediaMetrics.contentType,
    durationMillis: mediaMetrics.durationMillis,
    fileName: getFileName(asset, kind),
    height: mediaMetrics.height,
    kind,
    sizeBytes,
    width: mediaMetrics.width,
  };
}

async function uploadBlobWithProgress({
  blob,
  metadata,
  onUploadProgress,
  storageRef,
}: {
  blob: Blob;
  metadata: UploadMetadata;
  onUploadProgress?: (progress: number) => void;
  storageRef: ReturnType<typeof ref>;
}): Promise<UploadResult> {
  if (!onUploadProgress) {
    const snapshot = await uploadBytes(storageRef, blob, metadata);
    return snapshot;
  }

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, blob, metadata);

    task.on(
      'state_changed',
      (snapshot) => {
        const progress =
          snapshot.totalBytes > 0
            ? snapshot.bytesTransferred / snapshot.totalBytes
            : 0;
        onUploadProgress(progress);
      },
      reject,
      () => {
        onUploadProgress(1);
        resolve(task.snapshot);
      },
    );
  });
}

export function getMediaAspectRatio({
  fallback = 16 / 9,
  height,
  thumbnail,
  width,
}: {
  fallback?: number;
  height?: number | null;
  thumbnail?: MediaThumbnail | null;
  width?: number | null;
}) {
  if (width && height && width > 0 && height > 0) {
    return width / height;
  }

  if (
    thumbnail?.width &&
    thumbnail.height &&
    thumbnail.width > 0 &&
    thumbnail.height > 0
  ) {
    return thumbnail.width / thumbnail.height;
  }

  return fallback;
}

function clampAspectRatio({
  aspectRatio,
  max,
  min,
}: {
  aspectRatio: number;
  max: number;
  min: number;
}) {
  return Math.min(max, Math.max(min, aspectRatio));
}

export function getBoundedFeedImageAspectRatio(
  media: Pick<PostMedia, 'height' | 'thumbnail' | 'width'>,
) {
  return clampAspectRatio({
    aspectRatio: getMediaAspectRatio(media),
    max: MAX_FEED_IMAGE_ASPECT_RATIO,
    min: MIN_FEED_IMAGE_ASPECT_RATIO,
  });
}

export function getAssetAspectRatio(
  asset: ImagePickerAsset,
  fallback = 16 / 9,
) {
  return getMediaAspectRatio({
    fallback,
    height: normalizeAssetDimension(asset.height),
    width: normalizeAssetDimension(asset.width),
  });
}

export function getBoundedAssetImageAspectRatio(asset: ImagePickerAsset) {
  return clampAspectRatio({
    aspectRatio: getAssetAspectRatio(asset),
    max: MAX_FEED_IMAGE_ASPECT_RATIO,
    min: MIN_FEED_IMAGE_ASPECT_RATIO,
  });
}

function parseMediaThumbnail(value: unknown): MediaThumbnail | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (
    typeof data.storagePath !== 'string' ||
    typeof data.downloadURL !== 'string' ||
    data.contentType !== 'image/jpeg'
  ) {
    return null;
  }

  return {
    storagePath: data.storagePath,
    downloadURL: data.downloadURL,
    contentType: 'image/jpeg',
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : 0,
    width: typeof data.width === 'number' ? data.width : 0,
    height: typeof data.height === 'number' ? data.height : 0,
    generatedAt: (data.generatedAt as FirestoreDate | null | undefined) ?? null,
  };
}

function parseThumbnailStatus(value: unknown): MediaThumbnailStatus | null {
  return value === 'queued' ||
    value === 'processing' ||
    value === 'ready' ||
    value === 'failed'
    ? value
    : null;
}

function buildThumbnailFields(kind: PostMediaKind) {
  return {
    thumbnail: null,
    thumbnailStatus:
      kind === 'image' || kind === 'video' ? ('queued' as const) : null,
  };
}

function parsePostMedia(value: unknown): PostMedia | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (data.kind !== 'image' && data.kind !== 'video') {
    return null;
  }

  return {
    kind: data.kind,
    storagePath: String(data.storagePath ?? ''),
    downloadURL: String(data.downloadURL ?? ''),
    contentType: String(data.contentType ?? ''),
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : 0,
    width: typeof data.width === 'number' ? data.width : 0,
    height: typeof data.height === 'number' ? data.height : 0,
    durationMillis:
      typeof data.durationMillis === 'number' ? data.durationMillis : null,
    thumbnail: parseMediaThumbnail(data.thumbnail),
    thumbnailStatus: parseThumbnailStatus(data.thumbnailStatus),
    thumbnailError:
      typeof data.thumbnailError === 'string' ? data.thumbnailError : null,
  };
}

function parsePostMediaItem(value: unknown, fallbackOrder: number) {
  const media = parsePostMedia(value);

  if (!media) {
    return null;
  }

  const data = value as Record<string, unknown>;
  const id =
    typeof data.id === 'string' && data.id.trim()
      ? data.id.trim().slice(0, 80)
      : `media-${fallbackOrder}`;
  const order =
    typeof data.order === 'number' && Number.isFinite(data.order)
      ? Math.max(0, Math.floor(data.order))
      : fallbackOrder;

  return { ...media, id, order };
}

function parsePostMediaItems(
  value: unknown,
  legacyMedia: PostMedia | null,
): PostMediaItem[] {
  if (Array.isArray(value)) {
    const parsedItems = value
      .map((entry, index) => parsePostMediaItem(entry, index))
      .filter((entry): entry is PostMediaItem => entry !== null)
      .sort((left, right) => left.order - right.order)
      .slice(0, MAX_POST_PHOTO_ITEMS);

    if (parsedItems.length > 0) {
      return parsedItems;
    }
  }

  if (legacyMedia?.kind === 'image') {
    return [
      {
        ...legacyMedia,
        id: 'legacy-media',
        order: 0,
      },
    ];
  }

  return [];
}

function parsePostLinkCard(value: unknown): PostLinkCard | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (
    data.kind !== 'vehicle' ||
    typeof data.vehicleId !== 'string' ||
    typeof data.title !== 'string' ||
    typeof data.urlPath !== 'string' ||
    !data.vehicleId.trim() ||
    !data.title.trim() ||
    !data.urlPath.startsWith('/garage/')
  ) {
    return null;
  }

  return {
    kind: 'vehicle',
    imageURL: typeof data.imageURL === 'string' ? data.imageURL : null,
    subtitle: typeof data.subtitle === 'string' ? data.subtitle : null,
    title: data.title.trim(),
    urlPath: data.urlPath,
    vehicleId: data.vehicleId,
  };
}

function parseLinkPreview(value: unknown): LinkPreview | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (data.status !== 'ready' && data.status !== 'failed') {
    return null;
  }

  const url = typeof data.url === 'string' ? data.url.trim() : '';
  const displayUrl =
    typeof data.displayUrl === 'string' ? data.displayUrl.trim() : '';

  if (!url || !displayUrl) {
    return null;
  }

  return {
    status: data.status,
    url,
    displayUrl,
    title:
      typeof data.title === 'string' && data.title.trim()
        ? data.title.trim()
        : null,
    description:
      typeof data.description === 'string' && data.description.trim()
        ? data.description.trim()
        : null,
    imageURL:
      typeof data.imageURL === 'string' && data.imageURL.trim()
        ? data.imageURL.trim()
        : null,
    imageWidth:
      typeof data.imageWidth === 'number' &&
      Number.isFinite(data.imageWidth) &&
      data.imageWidth > 0
        ? Math.floor(data.imageWidth)
        : null,
    imageHeight:
      typeof data.imageHeight === 'number' &&
      Number.isFinite(data.imageHeight) &&
      data.imageHeight > 0
        ? Math.floor(data.imageHeight)
        : null,
    siteName:
      typeof data.siteName === 'string' && data.siteName.trim()
        ? data.siteName.trim()
        : null,
    fetchedAt:
      (data.fetchedAt as FirestoreDate | string | null | undefined) ?? null,
  };
}

function parseProfileMentions(value: unknown): ProfileMention[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const data = entry as Record<string, unknown>;

      if (
        typeof data.uid !== 'string' ||
        typeof data.label !== 'string' ||
        !data.uid.trim() ||
        !data.label.trim()
      ) {
        return null;
      }

      return {
        uid: data.uid.trim(),
        label: data.label.trim(),
      };
    })
    .filter((entry): entry is ProfileMention => entry !== null);
}

function parseFeedReasonCodes(value: unknown): FeedReasonCode[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const validReasons = new Set<FeedReasonCode>([
    'active_conversation',
    'fresh_unseen',
    'garage_build',
    'higher_engagement',
    'less_like_this_applied',
    'mutual_friend',
    'photo_or_video',
    'popular_with_frienzzi',
    'recent_activity',
    'recent_post',
    'similar_to_posts_you_open',
    'you_created_this',
    'you_follow_author',
  ]);

  return value
    .filter(
      (entry): entry is FeedReasonCode =>
        typeof entry === 'string' && validReasons.has(entry as FeedReasonCode),
    )
    .slice(0, 4);
}

function parseRankedFeedItem(
  value: Record<string, unknown>,
): NonNullable<PublicPost['ranking']> | null {
  const feedKind =
    value.feedKind === 'home' || value.feedKind === 'frienzzone'
      ? value.feedKind
      : null;

  if (!feedKind) {
    return null;
  }
  const rankingVersion =
    typeof value.rankingVersion === 'number' &&
    Number.isFinite(value.rankingVersion)
      ? Math.floor(value.rankingVersion)
      : 0;

  if (rankingVersion !== FEED_RANKING_VERSION) {
    return null;
  }

  const slot =
    value.slot === 'active_conversation' ||
    value.slot === 'exploration' ||
    value.slot === 'fresh_unseen' ||
    value.slot === 'high_engagement' ||
    value.slot === 'relationship'
      ? value.slot
      : 'exploration';
  const position =
    typeof value.position === 'number' &&
    Number.isFinite(value.position) &&
    value.position >= 0
      ? Math.floor(value.position)
      : Number.MAX_SAFE_INTEGER;

  return {
    feedKind,
    position,
    rankingVersion,
    reasonCodes: parseFeedReasonCodes(value.reasonCodes),
    score:
      typeof value.score === 'number' && Number.isFinite(value.score)
        ? value.score
        : 0,
    scoredAt: (value.scoredAt as FirestoreDate | null | undefined) ?? null,
    slot,
  };
}

function sanitizeProfileMentions(
  body: string,
  mentions: ProfileMention[] | undefined,
) {
  return profileMentionsForText(body, mentions) as ProfileMention[];
}

function postCreateSaveDiagnosticSummary({
  authorFullName,
  authorUid,
  body,
  linkPreviewSourceUrl,
  media,
  postKeys,
  profileMentions,
  reactionCountKeys,
  reshare,
  visibility,
}: {
  authorFullName: string | null;
  authorUid: string;
  body: string | null;
  linkPreviewSourceUrl?: string | null;
  media: PostMedia | null;
  postKeys: string[];
  profileMentions: ProfileMention[];
  reactionCountKeys: string[];
  reshare: PostReshare | null;
  visibility: PostVisibility;
}) {
  const mediaSummary = media
    ? [
        `media=${media.kind}`,
        `mediaKeys=${Object.keys(media).sort().join(',')}`,
        `storagePathLength=${media.storagePath.length}`,
        `downloadURLLength=${media.downloadURL.length}`,
        `contentType=${media.contentType}`,
        `sizeBytes=${media.sizeBytes}`,
        `width=${media.width ?? 'missing'}`,
        `height=${media.height ?? 'missing'}`,
        `durationMillis=${media.durationMillis ?? 'null'}`,
        `thumbnail=${media.thumbnail ? 'present' : 'null'}`,
        `thumbnailStatus=${media.thumbnailStatus ?? 'null'}`,
      ].join(', ')
    : 'media=none';

  return [
    'Debug: final post payload',
    `postKeys=${postKeys.sort().join(',')}`,
    `authorUidLength=${authorUid.length}`,
    `authorFullName=${
      typeof authorFullName === 'string' ? authorFullName.length : 'null'
    }`,
    `body=${body ? body.length : 'null'}`,
    `visibility=${visibility}`,
    `mentions=${profileMentions.length}`,
    `mentionKeys=${profileMentions
      .map((mention) => Object.keys(mention).sort().join(','))
      .join('|')}`,
    `reactionKeys=${reactionCountKeys.sort().join(',')}`,
    `linkPreview=${
      linkPreviewSourceUrl ? linkPreviewSourceUrl.length : 'missing'
    }`,
    `reshare=${reshare ? 'yes' : 'no'}`,
    mediaSummary,
  ].join('; ');
}

function parsePostData(id: string, data: Record<string, unknown>): PublicPost {
  const reactionCounts = parseReactionCounts(data.reactionCounts);
  const media = parsePostMedia(data.media);
  const legacyLikeCount =
    typeof data.likeCount === 'number' && Number.isFinite(data.likeCount)
      ? Math.max(0, Math.floor(data.likeCount))
      : 0;
  const hasReactionCounts =
    data.reactionCounts != null && typeof data.reactionCounts === 'object';

  if (!hasReactionCounts && legacyLikeCount > 0) {
    reactionCounts.like = legacyLikeCount;
  }

  const reactionCount =
    typeof data.reactionCount === 'number' &&
    Number.isFinite(data.reactionCount)
      ? Math.max(0, Math.floor(data.reactionCount))
      : totalReactionCounts(reactionCounts);

  return {
    id,
    authorUid: String(data.authorUid ?? ''),
    authorFullName:
      typeof data.authorFullName === 'string'
        ? data.authorFullName
        : typeof data.authorDisplayName === 'string'
          ? data.authorDisplayName
          : null,
    authorUsername:
      typeof data.authorUsername === 'string' ? data.authorUsername : null,
    authorPhotoURL:
      typeof data.authorPhotoURL === 'string' ? data.authorPhotoURL : null,
    authorFounderBadge: data.authorFounderBadge === true,
    authorVerifiedBadge: data.authorVerifiedBadge === true,
    body: typeof data.body === 'string' ? data.body : null,
    visibility: parsePostVisibility(data.visibility),
    media,
    mediaItems: parsePostMediaItems(data.mediaItems, media),
    linkCard: parsePostLinkCard(data.linkCard),
    linkPreview: parseLinkPreview(data.linkPreview),
    reshare: parsePostReshare(data.reshare),
    feedStats: parsePostFeedStats(data.feedStats),
    likeCount: legacyLikeCount,
    reactionCounts,
    reactionCount,
    ...(data.viewerReaction === null || isReaction(data.viewerReaction)
      ? { viewerReaction: data.viewerReaction }
      : {}),
    commentCount: typeof data.commentCount === 'number' ? data.commentCount : 0,
    shareCount:
      typeof data.shareCount === 'number' && Number.isFinite(data.shareCount)
        ? Math.max(0, Math.floor(data.shareCount))
        : 0,
    profileMentions: parseProfileMentions(data.profileMentions),
    createdAt: parsePublicDate(data.createdAt),
    editCount:
      typeof data.editCount === 'number' && Number.isFinite(data.editCount)
        ? Math.max(0, Math.floor(data.editCount))
        : 0,
    editedAt: parsePublicDate(data.editedAt),
    updatedAt: parsePublicDate(data.updatedAt),
  };
}

function parsePostReshare(value: unknown): PostReshare | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;
  const postId = typeof data.postId === 'string' ? data.postId.trim() : '';
  const authorUid =
    typeof data.authorUid === 'string' ? data.authorUid.trim() : '';

  if (!postId || !authorUid) {
    return null;
  }

  return {
    authorUid,
    postId,
  };
}

function parsePostSnapshot(snapshot: QuerySnapshot): PublicPost[] {
  return snapshot.docs.map((postSnapshot) =>
    parsePostData(postSnapshot.id, postSnapshot.data()),
  );
}

function parsePublicDate(value: unknown): FirestoreDate | null {
  if (value instanceof Date) {
    return value;
  }

  if (value && typeof value === 'object' && 'toMillis' in value) {
    return value as FirestoreDate;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function parseCommentMedia(media: unknown): CommentMedia | null {
  if (!media || typeof media !== 'object') {
    return null;
  }

  const data = media as Record<string, unknown>;

  if (
    data.source === 'upload' &&
    (data.kind === 'image' || data.kind === 'video')
  ) {
    return {
      kind: data.kind,
      source: 'upload',
      storagePath: String(data.storagePath ?? ''),
      downloadURL: String(data.downloadURL ?? ''),
      contentType: String(data.contentType ?? ''),
      sizeBytes:
        typeof data.sizeBytes === 'number' ? Number(data.sizeBytes) : 0,
      width: typeof data.width === 'number' ? Number(data.width) : 0,
      height: typeof data.height === 'number' ? Number(data.height) : 0,
      durationMillis:
        typeof data.durationMillis === 'number'
          ? Number(data.durationMillis)
          : null,
      thumbnail: parseMediaThumbnail(data.thumbnail),
      thumbnailStatus: parseThumbnailStatus(data.thumbnailStatus),
      thumbnailError:
        typeof data.thumbnailError === 'string' ? data.thumbnailError : null,
    };
  }

  if (data.source === 'giphy' && data.kind === 'gif') {
    return {
      kind: 'gif',
      source: 'giphy',
      giphyId: String(data.giphyId ?? ''),
      title: String(data.title ?? 'GIPHY GIF'),
      pageURL: String(data.pageURL ?? ''),
      previewURL: String(data.previewURL ?? ''),
      originalURL: String(data.originalURL ?? ''),
      width: typeof data.width === 'number' ? Number(data.width) : 0,
      height: typeof data.height === 'number' ? Number(data.height) : 0,
    };
  }

  return null;
}

function parseCommentData(
  id: string,
  data: Record<string, unknown>,
): PostComment {
  const reactionCounts = parseCommentReactionCounts(data.reactionCounts);
  const reactionCount =
    typeof data.reactionCount === 'number'
      ? Math.max(0, Math.floor(data.reactionCount))
      : totalCommentReactionCounts(reactionCounts);

  return {
    id,
    aiGenerated: data.aiGenerated === true,
    authorUid: String(data.authorUid ?? ''),
    authorFullName:
      typeof data.authorFullName === 'string'
        ? data.authorFullName
        : typeof data.authorDisplayName === 'string'
          ? data.authorDisplayName
          : null,
    authorUsername:
      typeof data.authorUsername === 'string' ? data.authorUsername : null,
    authorPhotoURL:
      typeof data.authorPhotoURL === 'string' ? data.authorPhotoURL : null,
    authorFounderBadge: data.authorFounderBadge === true,
    authorVerifiedBadge: data.authorVerifiedBadge === true,
    body: typeof data.body === 'string' ? data.body : null,
    botGenerated: data.botGenerated === true,
    media: parseCommentMedia(data.media),
    linkPreview: parseLinkPreview(data.linkPreview),
    parentCommentId:
      typeof data.parentCommentId === 'string' ? data.parentCommentId : null,
    profileMentions: parseProfileMentions(data.profileMentions),
    reactionCounts,
    reactionCount,
    replyToCommentId:
      typeof data.replyToCommentId === 'string' ? data.replyToCommentId : null,
    replyToFullName:
      typeof data.replyToFullName === 'string' ? data.replyToFullName : null,
    replyToUid: typeof data.replyToUid === 'string' ? data.replyToUid : null,
    replyToUsername:
      typeof data.replyToUsername === 'string' ? data.replyToUsername : null,
    createdAt: parsePublicDate(data.createdAt),
    localCreatedAtMillis:
      typeof data.localCreatedAtMillis === 'number' &&
      Number.isFinite(data.localCreatedAtMillis)
        ? data.localCreatedAtMillis
        : undefined,
    editCount:
      typeof data.editCount === 'number' && Number.isFinite(data.editCount)
        ? Math.max(0, Math.floor(data.editCount))
        : 0,
    editedAt: parsePublicDate(data.editedAt),
    updatedAt: parsePublicDate(data.updatedAt),
  };
}

function parseEditHistoryData(
  id: string,
  data: Record<string, unknown>,
): ContentEditHistoryEntry {
  const editNumber =
    typeof data.editNumber === 'number' && Number.isFinite(data.editNumber)
      ? Math.max(0, Math.floor(data.editNumber))
      : 0;

  return {
    id,
    editNumber,
    editedAt: (data.editedAt as FirestoreDate | null | undefined) ?? null,
    editorUid: String(data.editorUid ?? ''),
    nextBody: typeof data.nextBody === 'string' ? data.nextBody : null,
    nextProfileMentions: parseProfileMentions(data.nextProfileMentions),
    previousBody:
      typeof data.previousBody === 'string' ? data.previousBody : null,
    previousProfileMentions: parseProfileMentions(data.previousProfileMentions),
  };
}

function postCommentCreatedAtMillis(comment: PostComment) {
  if (typeof comment.localCreatedAtMillis === 'number') {
    return comment.localCreatedAtMillis;
  }

  const createdAt = comment.createdAt as unknown;

  if (!createdAt) {
    return 0;
  }

  if (createdAt instanceof Date) {
    return createdAt.getTime();
  }

  if (
    typeof createdAt === 'object' &&
    'toMillis' in createdAt &&
    typeof createdAt.toMillis === 'function'
  ) {
    return createdAt.toMillis();
  }

  return 0;
}

async function resolveReactionActors(snapshot: QuerySnapshot) {
  const actors = await Promise.all(
    snapshot.docs.map(async (reactionSnapshot) => {
      const data = reactionSnapshot.data();
      const reaction = data.reaction;

      if (!isReaction(reaction)) {
        return null;
      }

      const uid =
        typeof data.uid === 'string' && data.uid.length > 0
          ? data.uid
          : reactionSnapshot.id;
      const hasEmbeddedActor =
        Object.prototype.hasOwnProperty.call(data, 'actorFullName') ||
        Object.prototype.hasOwnProperty.call(data, 'actorPhotoURL');
      let fullName =
        typeof data.actorFullName === 'string' && data.actorFullName.length > 0
          ? data.actorFullName
          : 'Frienzzi user';
      let photoURL =
        typeof data.actorPhotoURL === 'string' && data.actorPhotoURL.length > 0
          ? data.actorPhotoURL
          : null;

      if (!hasEmbeddedActor) {
        try {
          const publicProfile = await getPublicProfile(uid);
          fullName = publicProfile?.fullName || fullName;
          photoURL = getProfileAvatarURL(publicProfile, 'thumb');
        } catch {
          // A missing public profile should not hide the reaction row.
        }
      }

      return {
        uid,
        reaction,
        fullName,
        photoURL,
        createdAt: (data.createdAt as FirestoreDate | null | undefined) ?? null,
        updatedAt: (data.updatedAt as FirestoreDate | null | undefined) ?? null,
      };
    }),
  );

  return actors.filter((actor): actor is ReactionActor => actor !== null);
}

function publicPostCreatedAtMillis(post: PublicPost) {
  return (
    post.localCreatedAtMillis ??
    (post.createdAt instanceof Date
      ? post.createdAt.getTime()
      : (post.createdAt?.toMillis() ?? 0))
  );
}

function sortPostsNewestFirst(posts: PublicPost[]) {
  return [...posts].sort((firstPost, secondPost) => {
    return (
      publicPostCreatedAtMillis(secondPost) -
      publicPostCreatedAtMillis(firstPost)
    );
  });
}

export function mergeRankedPostsWithLivePosts({
  limitCount,
  livePosts,
  rankedPosts,
}: {
  limitCount: number;
  livePosts: PublicPost[];
  rankedPosts: PublicPost[];
}) {
  const liveById = new Map(livePosts.map((post) => [post.id, post]));
  const rankedEntries = rankedPosts.map((rankedPost) => {
    const livePost = liveById.get(rankedPost.id);

    return livePost
      ? {
          ...livePost,
          ranking: rankedPost.ranking,
        }
      : rankedPost;
  });

  return rankedEntries
    .sort((firstPost, secondPost) => {
      const positionDifference =
        (firstPost.ranking?.position ?? Number.MAX_SAFE_INTEGER) -
        (secondPost.ranking?.position ?? Number.MAX_SAFE_INTEGER);

      if (positionDifference !== 0) {
        return positionDifference;
      }

      return (
        publicPostCreatedAtMillis(secondPost) -
        publicPostCreatedAtMillis(firstPost)
      );
    })
    .slice(0, limitCount);
}

function chunkValues<T>(values: T[], size: number) {
  const chunks: T[][] = [];

  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }

  return chunks;
}

function publishMergedPosts({
  limitCount,
  maps,
  onNext,
}: {
  limitCount: number;
  maps: Map<string, PublicPost>[];
  onNext: (posts: PublicPost[]) => void;
}) {
  const byId = new Map<string, PublicPost>();

  maps.forEach((postsMap) => {
    postsMap.forEach((post, postId) => {
      byId.set(postId, post);
    });
  });

  onNext(sortPostsNewestFirst([...byId.values()]).slice(0, limitCount));
}

function postsToMap(posts: PublicPost[]) {
  return new Map(posts.map((post) => [post.id, post]));
}

function subscribeToOrderedPostQuery({
  fallbackQuery,
  onError,
  onNext,
  postsQuery,
}: {
  fallbackQuery?: Query;
  onError: (error: Error) => void;
  onNext: (posts: PublicPost[]) => void;
  postsQuery: Query;
}) {
  let fallbackUnsubscribe: Unsubscribe | null = null;

  const orderedUnsubscribe = onSnapshot(
    postsQuery,
    (snapshot) => {
      onNext(parsePostSnapshot(snapshot));
    },
    (error: FirestoreError) => {
      if (error.code !== 'failed-precondition' || !fallbackQuery) {
        onError(error);
        return;
      }

      if (fallbackUnsubscribe) {
        return;
      }

      fallbackUnsubscribe = onSnapshot(
        fallbackQuery,
        (snapshot) => {
          onNext(sortPostsNewestFirst(parsePostSnapshot(snapshot)));
        },
        onError,
      );
    },
  );

  return () => {
    orderedUnsubscribe();
    fallbackUnsubscribe?.();
  };
}

function subscribeToMutualFriendUids({
  onError,
  onNext,
  uid,
}: {
  onError: (error: Error) => void;
  onNext: (mutualUids: string[], followingCount: number) => void;
  uid: string;
}) {
  const followingUids = new Set<string>();
  const followerUids = new Set<string>();
  let followingReady = false;
  let followersReady = false;

  function publishIfReady() {
    if (!followingReady || !followersReady) {
      return;
    }

    const mutualUids = [...followingUids].filter((targetUid) =>
      followerUids.has(targetUid),
    );

    onNext(mutualUids, followingUids.size);
  }

  const followingUnsubscribe = onSnapshot(
    collection(db, 'users', uid, 'following'),
    (snapshot) => {
      followingUids.clear();
      snapshot.docs.forEach((followingDoc) => {
        const data = followingDoc.data();
        const targetUid =
          typeof data.targetUid === 'string' ? data.targetUid : followingDoc.id;

        if (targetUid && targetUid !== uid) {
          followingUids.add(targetUid);
        }
      });
      followingReady = true;
      publishIfReady();
    },
    onError,
  );

  const followersUnsubscribe = onSnapshot(
    collection(db, 'users', uid, 'followers'),
    (snapshot) => {
      followerUids.clear();
      snapshot.docs.forEach((followerDoc) => {
        const data = followerDoc.data();
        const followerUid =
          typeof data.followerUid === 'string'
            ? data.followerUid
            : followerDoc.id;

        if (followerUid && followerUid !== uid) {
          followerUids.add(followerUid);
        }
      });
      followersReady = true;
      publishIfReady();
    },
    onError,
  );

  return () => {
    followingUnsubscribe();
    followersUnsubscribe();
  };
}

function subscribeToMutualState({
  currentUid,
  onError,
  onNext,
  targetUid,
}: {
  currentUid: string;
  onError: (error: Error) => void;
  onNext: (mutual: boolean) => void;
  targetUid: string;
}) {
  let currentFollowsTarget = false;
  let targetFollowsCurrent = false;
  let currentReady = false;
  let targetReady = false;

  function publishIfReady() {
    if (currentReady && targetReady) {
      onNext(currentFollowsTarget && targetFollowsCurrent);
    }
  }

  const currentFollowingUnsubscribe = onSnapshot(
    doc(db, 'users', currentUid, 'following', targetUid),
    (snapshot) => {
      currentFollowsTarget = snapshot.exists();
      currentReady = true;
      publishIfReady();
    },
    onError,
  );

  const targetFollowingUnsubscribe = onSnapshot(
    doc(db, 'users', targetUid, 'following', currentUid),
    (snapshot) => {
      targetFollowsCurrent = snapshot.exists();
      targetReady = true;
      publishIfReady();
    },
    onError,
  );

  return () => {
    currentFollowingUnsubscribe();
    targetFollowingUnsubscribe();
  };
}

function buildGiphyCommentMedia(gif: GiphyGif): GiphyCommentMedia {
  return {
    kind: 'gif',
    source: 'giphy',
    giphyId: gif.giphyId,
    title: gif.title,
    pageURL: gif.pageURL,
    previewURL: gif.previewURL,
    originalURL: gif.originalURL,
    width: gif.width,
    height: gif.height,
  };
}

export function subscribeToPublicPosts({
  limitCount = PUBLIC_POST_LIMIT,
  onError,
  onNext,
  uid,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (posts: PublicPost[]) => void;
  uid?: string;
}) {
  const publicPosts = new Map<string, PublicPost>();
  const ownPosts = new Map<string, PublicPost>();
  const friendsPosts = new Map<string, PublicPost>();
  const friendChunkPosts = new Map<number, PublicPost[]>();
  const unsubscribes: Unsubscribe[] = [];
  const friendPostUnsubscribes: Unsubscribe[] = [];
  let mutualUnsubscribe: Unsubscribe | null = null;

  function publish() {
    publishMergedPosts({
      limitCount,
      maps: [publicPosts, ownPosts, friendsPosts],
      onNext,
    });
  }

  function cleanupFriendPostSubscriptions() {
    while (friendPostUnsubscribes.length > 0) {
      friendPostUnsubscribes.pop()?.();
    }
    friendChunkPosts.clear();
    friendsPosts.clear();
  }

  unsubscribes.push(
    subscribeToOrderedPostQuery({
      postsQuery: query(
        collection(db, 'posts'),
        where('visibility', '==', 'public'),
        orderBy('createdAt', 'desc'),
        limit(limitCount),
      ),
      fallbackQuery: query(
        collection(db, 'posts'),
        where('visibility', '==', 'public'),
        limit(limitCount),
      ),
      onError,
      onNext: (posts) => {
        publicPosts.clear();
        postsToMap(posts).forEach((post, postId) =>
          publicPosts.set(postId, post),
        );
        publish();
      },
    }),
  );

  if (uid) {
    unsubscribes.push(
      subscribeToOrderedPostQuery({
        postsQuery: query(
          collection(db, 'posts'),
          where('authorUid', '==', uid),
          orderBy('createdAt', 'desc'),
          limit(limitCount),
        ),
        fallbackQuery: query(
          collection(db, 'posts'),
          where('authorUid', '==', uid),
          limit(limitCount),
        ),
        onError,
        onNext: (posts) => {
          ownPosts.clear();
          postsToMap(posts).forEach((post, postId) =>
            ownPosts.set(postId, post),
          );
          publish();
        },
      }),
    );

    mutualUnsubscribe = subscribeToMutualFriendUids({
      uid,
      onError,
      onNext: (mutualUids) => {
        cleanupFriendPostSubscriptions();

        if (mutualUids.length === 0) {
          publish();
          return;
        }

        chunkValues(mutualUids, FOLLOWING_POST_QUERY_CHUNK_SIZE).forEach(
          (mutualChunk, chunkIndex) => {
            const unsubscribe = subscribeToOrderedPostQuery({
              postsQuery: query(
                collection(db, 'posts'),
                where('visibility', '==', 'friends'),
                where('authorUid', 'in', mutualChunk),
                orderBy('createdAt', 'desc'),
                limit(limitCount),
              ),
              fallbackQuery: query(
                collection(db, 'posts'),
                where('visibility', '==', 'friends'),
                where('authorUid', 'in', mutualChunk),
                limit(limitCount),
              ),
              onError,
              onNext: (posts) => {
                friendChunkPosts.set(chunkIndex, posts);
                friendsPosts.clear();
                [...friendChunkPosts.values()].flat().forEach((post) => {
                  friendsPosts.set(post.id, post);
                });
                publish();
              },
            });
            friendPostUnsubscribes.push(unsubscribe);
          },
        );
      },
    });
  }

  return () => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
    mutualUnsubscribe?.();
    cleanupFriendPostSubscriptions();
  };
}

export function subscribeToRankedPosts({
  feedKind,
  limitCount = PUBLIC_POST_LIMIT,
  onError,
  onNext,
  uid,
}: {
  feedKind: FeedKind;
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (posts: PublicPost[]) => void;
  uid: string;
}) {
  return onSnapshot(
    query(
      collection(db, 'users', uid, 'rankedFeeds', feedKind, 'items'),
      orderBy('position', 'asc'),
      limit(limitCount),
    ),
    (snapshot) => {
      void Promise.all(
        snapshot.docs.map(async (rankedSnapshot) => {
          const rankedData = rankedSnapshot.data();
          const ranking = parseRankedFeedItem(rankedData);

          if (!ranking) {
            return null;
          }

          try {
            const postRef = doc(db, 'posts', rankedSnapshot.id);
            let postSnapshot: DocumentSnapshot<DocumentData>;

            try {
              postSnapshot = await getDocFromServer(postRef);
            } catch {
              postSnapshot = await getDoc(postRef);
            }

            if (!postSnapshot.exists()) {
              return null;
            }

            return {
              ...parsePostData(postSnapshot.id, postSnapshot.data()),
              ranking,
            };
          } catch {
            return null;
          }
        }),
      )
        .then((rankedPosts) => {
          const visiblePosts: PublicPost[] = [];

          rankedPosts.forEach((post) => {
            if (post) {
              visiblePosts.push(post);
            }
          });

          onNext(visiblePosts);
        })
        .catch(onError);
    },
    onError,
  );
}

export function subscribeToPublicOnlyPosts({
  limitCount = PUBLIC_POST_LIMIT,
  onError,
  onNext,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (posts: PublicPost[]) => void;
}) {
  return subscribeToOrderedPostQuery({
    postsQuery: query(
      collection(db, 'posts'),
      where('visibility', '==', 'public'),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ),
    fallbackQuery: query(
      collection(db, 'posts'),
      where('visibility', '==', 'public'),
      limit(limitCount),
    ),
    onError,
    onNext,
  });
}

export async function getPost(postId: string): Promise<PublicPost | null> {
  const postSnapshot = await getDoc(doc(db, 'posts', postId));

  if (!postSnapshot.exists()) {
    return null;
  }

  return parsePostData(postSnapshot.id, postSnapshot.data());
}

export async function updatePostVisibility(
  postId: string,
  visibility: PostVisibility,
) {
  await updateDoc(doc(db, 'posts', postId), {
    updatedAt: serverTimestamp(),
    visibility,
  });
}

export async function getPublicPostDetail(postId: string): Promise<{
  comments: PostComment[];
  post: PublicPost | null;
}> {
  const { data: payload, response } = await fetchPublicJson<{
    comments?: (Record<string, unknown> & { id?: unknown })[];
    post?: (Record<string, unknown> & { id?: unknown }) | null;
  }>(`/api/public/posts/${encodeURIComponent(postId)}`);

  if (response.status === 404) {
    return { comments: [], post: null };
  }

  if (!response.ok) {
    throw new Error('Unable to load this post.');
  }

  const post =
    payload.post && typeof payload.post.id === 'string'
      ? parsePostData(payload.post.id, payload.post)
      : null;
  const comments = Array.isArray(payload.comments)
    ? payload.comments
        .filter((comment) => typeof comment.id === 'string')
        .map((comment) => parseCommentData(String(comment.id), comment))
        .sort((firstComment, secondComment) => {
          return (
            postCommentCreatedAtMillis(secondComment) -
            postCommentCreatedAtMillis(firstComment)
          );
        })
    : [];

  return { comments, post };
}

export function subscribeToPost({
  onError,
  onNext,
  postId,
}: {
  onError: (error: Error) => void;
  onNext: (post: PublicPost | null) => void;
  postId: string;
}) {
  return onSnapshot(
    doc(db, 'posts', postId),
    (snapshot) => {
      onNext(
        snapshot.exists() ? parsePostData(snapshot.id, snapshot.data()) : null,
      );
    },
    onError,
  );
}

export function subscribeToUserPosts({
  currentUid,
  limitCount = PUBLIC_POST_LIMIT,
  onError,
  onNext,
  uid,
}: {
  currentUid?: string;
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (posts: PublicPost[]) => void;
  uid: string;
}) {
  if (currentUid && currentUid === uid) {
    return subscribeToOrderedPostQuery({
      postsQuery: query(
        collection(db, 'posts'),
        where('authorUid', '==', uid),
        orderBy('createdAt', 'desc'),
        limit(limitCount),
      ),
      fallbackQuery: query(
        collection(db, 'posts'),
        where('authorUid', '==', uid),
        limit(limitCount),
      ),
      onError,
      onNext,
    });
  }

  const publicPosts = new Map<string, PublicPost>();
  const friendsPosts = new Map<string, PublicPost>();
  let friendUnsubscribe: Unsubscribe | null = null;
  let mutualUnsubscribe: Unsubscribe | null = null;

  function publish() {
    publishMergedPosts({
      limitCount,
      maps: [publicPosts, friendsPosts],
      onNext,
    });
  }

  const publicUnsubscribe = subscribeToOrderedPostQuery({
    postsQuery: query(
      collection(db, 'posts'),
      where('visibility', '==', 'public'),
      where('authorUid', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ),
    fallbackQuery: query(
      collection(db, 'posts'),
      where('visibility', '==', 'public'),
      where('authorUid', '==', uid),
      limit(limitCount),
    ),
    onError,
    onNext: (posts) => {
      publicPosts.clear();
      postsToMap(posts).forEach((post, postId) =>
        publicPosts.set(postId, post),
      );
      publish();
    },
  });

  if (currentUid) {
    mutualUnsubscribe = subscribeToMutualState({
      currentUid,
      targetUid: uid,
      onError,
      onNext: (mutual) => {
        friendUnsubscribe?.();
        friendUnsubscribe = null;
        friendsPosts.clear();

        if (!mutual) {
          publish();
          return;
        }

        friendUnsubscribe = subscribeToOrderedPostQuery({
          postsQuery: query(
            collection(db, 'posts'),
            where('visibility', '==', 'friends'),
            where('authorUid', '==', uid),
            orderBy('createdAt', 'desc'),
            limit(limitCount),
          ),
          fallbackQuery: query(
            collection(db, 'posts'),
            where('visibility', '==', 'friends'),
            where('authorUid', '==', uid),
            limit(limitCount),
          ),
          onError,
          onNext: (posts) => {
            friendsPosts.clear();
            postsToMap(posts).forEach((post, postId) =>
              friendsPosts.set(postId, post),
            );
            publish();
          },
        });
      },
    });
  }

  return () => {
    publicUnsubscribe();
    mutualUnsubscribe?.();
    friendUnsubscribe?.();
  };
}

export function subscribeToFollowingPosts({
  limitCount = PUBLIC_POST_LIMIT,
  onError,
  onNext,
  uid,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (result: { followingCount: number; posts: PublicPost[] }) => void;
  uid: string;
}) {
  const followingRef = collection(db, 'users', uid, 'following');
  const publicPostUnsubscribes: Unsubscribe[] = [];
  const mutualPostUnsubscribes: Unsubscribe[] = [];
  const chunkPosts = new Map<number, PublicPost[]>();
  const mutualFriendPosts = new Map<number, PublicPost[]>();
  const readyPublicChunks = new Set<number>();
  const readyMutualChunks = new Set<number>();
  let mutualUnsubscribe: Unsubscribe | null = null;
  let currentFollowingCount = 0;
  let expectedPublicChunks = 0;
  let expectedMutualChunks = 0;
  let publicReady = false;
  let mutualReady = false;

  function cleanupPublicPostSubscriptions() {
    while (publicPostUnsubscribes.length > 0) {
      publicPostUnsubscribes.pop()?.();
    }
    chunkPosts.clear();
    readyPublicChunks.clear();
  }

  function cleanupMutualPostSubscriptions() {
    while (mutualPostUnsubscribes.length > 0) {
      mutualPostUnsubscribes.pop()?.();
    }
    mutualFriendPosts.clear();
    readyMutualChunks.clear();
  }

  function preparePublicChunks(chunkCount: number) {
    expectedPublicChunks = chunkCount;
    readyPublicChunks.clear();
    publicReady = chunkCount === 0;
  }

  function prepareMutualChunks(chunkCount: number) {
    expectedMutualChunks = chunkCount;
    readyMutualChunks.clear();
    mutualReady = chunkCount === 0;
  }

  function markPublicChunkReady(chunkIndex: number) {
    readyPublicChunks.add(chunkIndex);
    publicReady = readyPublicChunks.size >= expectedPublicChunks;
  }

  function markMutualChunkReady(chunkIndex: number) {
    readyMutualChunks.add(chunkIndex);
    mutualReady = readyMutualChunks.size >= expectedMutualChunks;
  }

  function publish() {
    if (!publicReady || !mutualReady) {
      return;
    }

    const publicPosts = [...chunkPosts.values()].flat();
    const friendsPosts = [...mutualFriendPosts.values()].flat();
    const postsById = new Map<string, PublicPost>();

    [...publicPosts, ...friendsPosts].forEach((post) => {
      postsById.set(post.id, post);
    });

    onNext({
      followingCount: currentFollowingCount,
      posts: sortPostsNewestFirst([...postsById.values()]).slice(0, limitCount),
    });
  }

  mutualUnsubscribe = subscribeToMutualFriendUids({
    uid,
    onError,
    onNext: (mutualUids, followingCount) => {
      currentFollowingCount = followingCount;
      cleanupMutualPostSubscriptions();

      const mutualChunks = chunkValues(
        mutualUids,
        FOLLOWING_POST_QUERY_CHUNK_SIZE,
      );
      prepareMutualChunks(mutualChunks.length);

      mutualChunks.forEach((mutualChunk, chunkIndex) => {
        const unsubscribe = subscribeToOrderedPostQuery({
          postsQuery: query(
            collection(db, 'posts'),
            where('visibility', '==', 'friends'),
            where('authorUid', 'in', mutualChunk),
            orderBy('createdAt', 'desc'),
            limit(limitCount),
          ),
          fallbackQuery: query(
            collection(db, 'posts'),
            where('visibility', '==', 'friends'),
            where('authorUid', 'in', mutualChunk),
            limit(limitCount),
          ),
          onError,
          onNext: (posts) => {
            mutualFriendPosts.set(chunkIndex, posts);
            markMutualChunkReady(chunkIndex);
            publish();
          },
        });

        mutualPostUnsubscribes.push(unsubscribe);
      });

      if (mutualChunks.length === 0) {
        publish();
      }
    },
  });

  const followingUnsubscribe = onSnapshot(
    followingRef,
    (followingSnapshot) => {
      cleanupPublicPostSubscriptions();
      currentFollowingCount = followingSnapshot.size;

      const followingUids = followingSnapshot.docs
        .map((followingDoc) => {
          const data = followingDoc.data();
          return typeof data.targetUid === 'string'
            ? data.targetUid
            : followingDoc.id;
        })
        .filter((targetUid) => targetUid && targetUid !== uid);

      if (followingUids.length === 0) {
        preparePublicChunks(0);
        publish();
        return;
      }

      const chunks = chunkValues(
        followingUids,
        FOLLOWING_POST_QUERY_CHUNK_SIZE,
      );
      preparePublicChunks(chunks.length);

      chunks.forEach((followingChunk, chunkIndex) => {
        const unsubscribe = subscribeToOrderedPostQuery({
          postsQuery: query(
            collection(db, 'posts'),
            where('visibility', '==', 'public'),
            where('authorUid', 'in', followingChunk),
            orderBy('createdAt', 'desc'),
            limit(limitCount),
          ),
          fallbackQuery: query(
            collection(db, 'posts'),
            where('visibility', '==', 'public'),
            where('authorUid', 'in', followingChunk),
            limit(limitCount),
          ),
          onError,
          onNext: (posts) => {
            chunkPosts.set(chunkIndex, posts);
            markPublicChunkReady(chunkIndex);
            publish();
          },
        });

        publicPostUnsubscribes.push(unsubscribe);
      });
    },
    onError,
  );

  return () => {
    followingUnsubscribe();
    mutualUnsubscribe?.();
    cleanupPublicPostSubscriptions();
    cleanupMutualPostSubscriptions();
  };
}

export function subscribeToPostReaction({
  onError,
  onNext,
  postId,
  uid,
}: {
  onError: (error: Error) => void;
  onNext: (reaction: PostReaction | null, mutationId: string | null) => void;
  postId: string;
  uid: string;
}) {
  const reactionRef = doc(db, 'posts', postId, 'reactions', uid);
  const legacyLikeRef = doc(db, 'posts', postId, 'likes', uid);
  let currentReaction: PostReaction | null | undefined;
  let currentMutationId: string | null = null;
  let legacyLiked: boolean | undefined;

  function emitIfReady() {
    if (currentReaction === undefined || legacyLiked === undefined) {
      return;
    }

    onNext(currentReaction ?? (legacyLiked ? 'like' : null), currentMutationId);
  }

  const reactionUnsubscribe = onSnapshot(
    reactionRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        currentReaction = null;
        currentMutationId = null;
        emitIfReady();
        return;
      }

      const data = snapshot.data();
      const reaction = data.reaction;
      currentReaction = isReaction(reaction) ? reaction : null;
      currentMutationId =
        typeof data.mutationId === 'string' && data.mutationId
          ? data.mutationId
          : null;
      emitIfReady();
    },
    onError,
  );

  const legacyLikeUnsubscribe = onSnapshot(
    legacyLikeRef,
    (snapshot) => {
      legacyLiked = snapshot.exists();
      emitIfReady();
    },
    onError,
  );

  return () => {
    reactionUnsubscribe();
    legacyLikeUnsubscribe();
  };
}

export function subscribeToPostReactionActors({
  limitCount = 50,
  onError,
  onNext,
  postId,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (actors: ReactionActor[]) => void;
  postId: string;
}) {
  let active = true;
  const reactionsQuery = query(
    collection(db, 'posts', postId, 'reactions'),
    orderBy('updatedAt', 'desc'),
    limit(limitCount),
  );

  const unsubscribe = onSnapshot(
    reactionsQuery,
    (snapshot) => {
      void resolveReactionActors(snapshot)
        .then((actors) => {
          if (active) {
            onNext(actors);
          }
        })
        .catch((error: Error) => {
          if (active) {
            onError(error);
          }
        });
    },
    onError,
  );

  return () => {
    active = false;
    unsubscribe();
  };
}

export function subscribeToPostLike({
  onError,
  onNext,
  postId,
  uid,
}: {
  onError: (error: Error) => void;
  onNext: (liked: boolean) => void;
  postId: string;
  uid: string;
}) {
  return subscribeToPostReaction({
    onError,
    onNext: (reaction) => onNext(reaction === 'like'),
    postId,
    uid,
  });
}

export function subscribeToCommentReaction({
  commentId,
  onError,
  onNext,
  postId,
  uid,
}: {
  commentId: string;
  onError: (error: Error) => void;
  onNext: (reaction: CommentReaction | null, mutationId: string | null) => void;
  postId: string;
  uid: string;
}) {
  const reactionRef = doc(
    db,
    'posts',
    postId,
    'comments',
    commentId,
    'reactions',
    uid,
  );

  return onSnapshot(
    reactionRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        onNext(null, null);
        return;
      }

      const data = snapshot.data();
      const reaction = data.reaction;
      const mutationId =
        typeof data.mutationId === 'string' && data.mutationId
          ? data.mutationId
          : null;
      onNext(isCommentReaction(reaction) ? reaction : null, mutationId);
    },
    onError,
  );
}

export function subscribeToCommentReactionActors({
  commentId,
  limitCount = 50,
  onError,
  onNext,
  postId,
}: {
  commentId: string;
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (actors: ReactionActor[]) => void;
  postId: string;
}) {
  let active = true;
  const reactionsQuery = query(
    collection(db, 'posts', postId, 'comments', commentId, 'reactions'),
    orderBy('updatedAt', 'desc'),
    limit(limitCount),
  );

  const unsubscribe = onSnapshot(
    reactionsQuery,
    (snapshot) => {
      void resolveReactionActors(snapshot)
        .then((actors) => {
          if (active) {
            onNext(actors);
          }
        })
        .catch((error: Error) => {
          if (active) {
            onError(error);
          }
        });
    },
    onError,
  );

  return () => {
    active = false;
    unsubscribe();
  };
}

export function subscribeToPostComments({
  limitCount = 50,
  onError,
  onNext,
  postId,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (comments: PostComment[]) => void;
  postId: string;
}) {
  let active = true;

  void getPostComments({ limitCount, postId })
    .then((comments) => {
      if (active) {
        onNext(comments);
      }
    })
    .catch((error) => {
      if (active) {
        onError(
          error instanceof Error
            ? error
            : new Error('Unable to load comments.'),
        );
      }
    });

  return () => {
    active = false;
  };
}

function commentMutationTimestampFingerprint(value: unknown) {
  if (!value || typeof value !== 'object') {
    return 'unknown';
  }

  const timestamp = value as {
    nanoseconds?: unknown;
    seconds?: unknown;
    toMillis?: unknown;
  };

  if (typeof timestamp.toMillis === 'function') {
    return String(timestamp.toMillis());
  }

  if (typeof timestamp.seconds === 'number') {
    const nanoseconds =
      typeof timestamp.nanoseconds === 'number' ? timestamp.nanoseconds : 0;

    return `${timestamp.seconds}:${nanoseconds}`;
  }

  return 'unknown';
}

function postCommentMutationFingerprint(data: Record<string, unknown>) {
  const commentCount =
    typeof data.commentCount === 'number' && Number.isFinite(data.commentCount)
      ? Math.max(0, Math.trunc(data.commentCount))
      : 0;
  const mutation =
    data.commentMutation && typeof data.commentMutation === 'object'
      ? (data.commentMutation as Record<string, unknown>)
      : null;

  if (!mutation) {
    return `count:${commentCount}:none`;
  }

  const commentId =
    typeof mutation.commentId === 'string' ? mutation.commentId : 'unknown';
  const operation =
    typeof mutation.operation === 'string' ? mutation.operation : 'unknown';
  const updatedAt = commentMutationTimestampFingerprint(mutation.updatedAt);

  return `count:${commentCount}:${operation}:${commentId}:${updatedAt}`;
}

export function subscribeToPostCommentMutations({
  onError,
  onNext,
  postId,
}: {
  onError: (error: Error) => void;
  onNext: () => void;
  postId: string;
}) {
  let hasInitialSnapshot = false;
  let previousFingerprint: string | null = null;

  return onSnapshot(
    doc(db, 'posts', postId),
    (snapshot) => {
      const nextFingerprint = snapshot.exists()
        ? postCommentMutationFingerprint(
            snapshot.data() as Record<string, unknown>,
          )
        : null;

      if (!hasInitialSnapshot) {
        hasInitialSnapshot = true;
        previousFingerprint = nextFingerprint;
        return;
      }

      if (nextFingerprint !== previousFingerprint) {
        previousFingerprint = nextFingerprint;
        onNext();
        return;
      }

      previousFingerprint = nextFingerprint;
    },
    onError,
  );
}

export async function getPostComments({
  limitCount = 50,
  postId,
}: {
  limitCount?: number;
  postId: string;
}) {
  const result = await getPostCommentsCallable({ limitCount, postId });

  return Array.isArray(result.data.comments)
    ? result.data.comments.map((comment) =>
        parseCommentData(String(comment.id ?? ''), comment),
      )
    : [];
}

export async function togglePostLike(post: PublicPost, uid: string) {
  return setPostLike(post, uid, undefined);
}

export async function setPostLike(
  post: PublicPost,
  uid: string,
  liked: boolean | undefined,
) {
  const nextReaction =
    liked === undefined
      ? post.reactionCounts.like > 0
        ? null
        : 'like'
      : liked
        ? 'like'
        : null;

  return setPostReaction({
    post,
    reaction: nextReaction,
    uid,
  });
}

export async function setPostReaction({
  mutationId,
  post,
  reaction,
}: {
  mutationId?: string;
  post: PublicPost;
  reaction: PostReaction | null;
  uid: string;
}) {
  if (reaction !== null && !isReaction(reaction)) {
    throw new Error('That post reaction is not supported.');
  }

  await setPostReactionCallable({
    ...(mutationId ? { mutationId } : {}),
    postId: post.id,
    reaction,
  });
}

export async function setCommentReaction({
  commentId,
  mutationId,
  postId,
  reaction,
}: {
  commentId: string;
  mutationId?: string;
  postId: string;
  reaction: CommentReaction | null;
  uid: string;
}) {
  if (reaction !== null && !isCommentReaction(reaction)) {
    throw new Error('That comment reaction is not supported.');
  }

  await setPostCommentReactionCallable({
    commentId,
    ...(mutationId ? { mutationId } : {}),
    postId,
    reaction,
  });
}

export async function createPostComment({
  authorUid,
  body,
  giphyGif,
  mediaAsset,
  mediaKind,
  onUploadProgress,
  parentCommentId,
  replyToCommentId,
  postId,
  profileMentions,
}: CreatePostCommentInput) {
  const trimmedBody = body.trim();
  const sanitizedMentions = sanitizeProfileMentions(
    trimmedBody,
    profileMentions,
  );

  if (!trimmedBody && !mediaAsset && !giphyGif) {
    throw new Error('Add a comment before posting.');
  }

  if (trimmedBody.length > MAX_COMMENT_BODY_LENGTH) {
    throw new Error('Comments must be 1,000 characters or fewer.');
  }

  const postRef = doc(db, 'posts', postId);
  const commentRef = doc(collection(postRef, 'comments'));
  let media: CommentMedia | null = giphyGif
    ? buildGiphyCommentMedia(giphyGif)
    : null;

  if (mediaAsset && mediaKind) {
    const preparedImage =
      mediaKind === 'image' ? await preparePostImageUpload(mediaAsset) : null;
    const mediaMetrics =
      mediaKind === 'video' ? validateMedia(mediaAsset, mediaKind) : null;
    const fileName = preparedImage
      ? preparedImage.fileName
      : getFileName(mediaAsset, mediaKind);
    const storagePath = `comments/${authorUid}/${postId}/${commentRef.id}/${fileName}`;
    const blob =
      preparedImage?.blob ??
      (await readOriginalMediaBlob(mediaAsset, mediaKind));
    const contentType = preparedImage
      ? preparedImage.contentType
      : mediaMetrics!.contentType;
    const finalSizeBytes = preparedImage
      ? preparedImage.sizeBytes
      : mediaMetrics!.sizeBytes || normalizeMediaSizeBytes(blob.size);
    const metadata: UploadMetadata = { contentType };
    const storageRef = ref(storage, storagePath);
    const width = preparedImage ? preparedImage.width : mediaMetrics!.width;
    const height = preparedImage ? preparedImage.height : mediaMetrics!.height;
    const durationMillis = preparedImage ? null : mediaMetrics!.durationMillis;

    validateMediaSize(finalSizeBytes, mediaKind);

    const uploadSnapshot = await uploadBlobWithProgress({
      blob,
      metadata,
      onUploadProgress,
      storageRef,
    });

    media = {
      kind: mediaKind,
      source: 'upload',
      storagePath,
      downloadURL: await getDownloadURL(uploadSnapshot.ref),
      contentType,
      sizeBytes: finalSizeBytes,
      width,
      height,
      durationMillis,
      ...buildThumbnailFields(mediaKind),
    };
  }

  await createPostCommentCallable({
    body: trimmedBody,
    commentId: commentRef.id,
    media,
    parentCommentId: parentCommentId ?? null,
    replyToCommentId: replyToCommentId ?? null,
    postId,
    profileMentions: sanitizedMentions,
  });
}

export async function editPost({
  body,
  postId,
  profileMentions,
}: {
  body: string;
  postId: string;
  profileMentions?: ProfileMention[];
}) {
  const trimmedBody = body.trim();
  const sanitizedMentions = sanitizeProfileMentions(
    trimmedBody,
    profileMentions,
  );

  if (trimmedBody.length > MAX_POST_BODY_LENGTH) {
    throw new Error('Posts must be 2,000 characters or fewer.');
  }

  await editPostCallable({
    body: trimmedBody,
    postId,
    profileMentions: sanitizedMentions,
  });
}

export async function editPostComment({
  body,
  commentId,
  postId,
  profileMentions,
}: {
  body: string;
  commentId: string;
  postId: string;
  profileMentions?: ProfileMention[];
}) {
  const trimmedBody = body.trim();
  const sanitizedMentions = sanitizeProfileMentions(
    trimmedBody,
    profileMentions,
  );

  if (trimmedBody.length > MAX_COMMENT_BODY_LENGTH) {
    throw new Error('Comments must be 1,000 characters or fewer.');
  }

  await editPostCommentCallable({
    body: trimmedBody,
    commentId,
    postId,
    profileMentions: sanitizedMentions,
  });
}

export async function loadPostEditHistory(postId: string) {
  const historyQuery = query(
    collection(db, 'posts', postId, 'editHistory'),
    orderBy('editNumber', 'desc'),
    limit(50),
  );
  const snapshot = await getDocs(historyQuery);

  return snapshot.docs.map((historySnapshot) =>
    parseEditHistoryData(historySnapshot.id, historySnapshot.data()),
  );
}

export async function loadPostCommentEditHistory({
  commentId,
  postId,
}: {
  commentId: string;
  postId: string;
}) {
  const historyQuery = query(
    collection(db, 'posts', postId, 'comments', commentId, 'editHistory'),
    orderBy('editNumber', 'desc'),
    limit(50),
  );
  const snapshot = await getDocs(historyQuery);

  return snapshot.docs.map((historySnapshot) =>
    parseEditHistoryData(historySnapshot.id, historySnapshot.data()),
  );
}

export async function deletePostComment({
  commentId,
  postId,
}: {
  commentId: string;
  postId: string;
  uid?: string;
}) {
  await deletePostCommentCallable({ commentId, postId });
}

export async function deletePost(postId: string) {
  const result = await deletePostCallable({ postId });
  return result.data;
}

export async function recordPostShare(postId: string) {
  const result = await recordPostShareCallable({ postId });
  return result.data;
}

export async function recordFeedEvents(events: FeedEventUpload[]) {
  if (events.length === 0) {
    return;
  }

  await recordFeedEventsCallable({ events });
}

export async function refreshMyRankedFeeds(feedKind?: FeedKind) {
  const result = await refreshMyRankedFeedsCallable({
    ...(feedKind ? { feedKind } : {}),
  });

  return result.data;
}

export async function getFeedPosts({
  feedKind,
  limitCount,
}: {
  feedKind: FeedKind;
  limitCount?: number;
}) {
  const result = await getFeedPostsCallable({ feedKind, limitCount });
  const posts = Array.isArray(result.data.posts)
    ? result.data.posts
        .filter(
          (post): post is Record<string, unknown> & { id: string } =>
            typeof post.id === 'string',
        )
        .map((post) => parsePostData(post.id, post))
    : [];

  return {
    followingCount:
      typeof result.data.followingCount === 'number'
        ? Math.max(0, Math.floor(result.data.followingCount))
        : 0,
    posts,
  };
}

export function refreshMyRankedFeedsWithCooldown({
  feedKind,
  force = false,
  minimumIntervalMs = RANKED_FEED_CLIENT_REFRESH_INTERVAL_MS,
  uid,
}: {
  feedKind: FeedKind;
  force?: boolean;
  minimumIntervalMs?: number;
  uid: string;
}) {
  const cacheKey = `${uid}:${feedKind}`;
  const now = Date.now();
  const currentState = rankedFeedRefreshState.get(cacheKey);

  if (currentState?.inFlight) {
    return currentState.inFlight;
  }

  if (
    !force &&
    currentState &&
    now - currentState.lastStartedAt < minimumIntervalMs
  ) {
    return Promise.resolve();
  }

  const inFlight = refreshMyRankedFeeds(feedKind)
    .then(() => undefined)
    .finally(() => {
      const nextState = rankedFeedRefreshState.get(cacheKey);

      if (nextState?.inFlight === inFlight) {
        rankedFeedRefreshState.set(cacheKey, {
          lastStartedAt: nextState.lastStartedAt,
        });
      }
    });

  rankedFeedRefreshState.set(cacheKey, {
    inFlight,
    lastStartedAt: now,
  });

  return inFlight;
}

export function feedReasonLabel(reasonCode: FeedReasonCode) {
  switch (reasonCode) {
    case 'active_conversation':
      return 'Active conversation';
    case 'fresh_unseen':
      return 'Fresh for you';
    case 'garage_build':
      return 'Garage build';
    case 'higher_engagement':
      return 'High engagement';
    case 'less_like_this_applied':
      return 'Adjusted from your feedback';
    case 'mutual_friend':
      return 'Mutual friend';
    case 'photo_or_video':
      return 'Photo or video';
    case 'popular_with_frienzzi':
      return 'Popular on Frienzzi';
    case 'recent_activity':
      return 'Recent activity';
    case 'recent_post':
      return 'Recent post';
    case 'similar_to_posts_you_open':
      return 'Similar to posts you open';
    case 'you_created_this':
      return 'Your post';
    case 'you_follow_author':
      return 'You follow this person';
  }
}

export async function searchMentionProfiles({
  postId,
  query,
  scope,
  vehicleId,
}: {
  postId?: string;
  query: string;
  scope: 'post' | 'comment' | 'vehicle_comment';
  vehicleId?: string;
}) {
  const result = await searchMentionProfilesCallable({
    ...(postId ? { postId } : {}),
    ...(vehicleId ? { vehicleId } : {}),
    query,
    scope,
  });

  return result.data.profiles;
}

export async function createPost({
  authorFullName,
  authorUid,
  body,
  debugDiagnostics = false,
  linkPreviewSourceUrl,
  mediaAsset,
  mediaAssets,
  mediaKind,
  onUploadProgress,
  preparedMediaUpload,
  preparedMediaUploads,
  profileMentions,
  reshareOfPost,
  visibility = 'public',
}: CreatePostInput) {
  const trimmedBody = body.trim();
  const sanitizedMentions = sanitizeProfileMentions(
    trimmedBody,
    profileMentions,
  );
  const reshare = reshareOfPost
    ? {
        authorUid: reshareOfPost.authorUid,
        postId: reshareOfPost.id,
      }
    : null;
  const imageMediaAssets =
    mediaKind === 'image'
      ? mediaAssets && mediaAssets.length > 0
        ? mediaAssets
        : mediaAsset
          ? [mediaAsset]
          : []
      : [];
  const hasMediaAsset =
    mediaKind === 'video' ? Boolean(mediaAsset) : imageMediaAssets.length > 0;

  if (!trimmedBody && !hasMediaAsset && !linkPreviewSourceUrl && !reshare) {
    throw new Error(
      'Add text, a link, a photo, a video, or a post to reshare before posting.',
    );
  }

  if (trimmedBody.length > MAX_POST_BODY_LENGTH) {
    throw new Error('Posts must be 2,000 characters or fewer.');
  }

  if (!isPostVisibility(visibility)) {
    throw new Error('Choose a valid post visibility.');
  }

  if (mediaKind === 'image' && imageMediaAssets.length > MAX_POST_PHOTO_ITEMS) {
    throw new Error(`Choose up to ${MAX_POST_PHOTO_ITEMS} photos per post.`);
  }

  if (mediaKind === 'video' && mediaAssets && mediaAssets.length > 0) {
    throw new Error('Videos can only be added one at a time.');
  }

  const postRef = doc(collection(db, 'posts'));
  let media: PostMedia | null = null;
  let mediaItems: PostMediaItem[] = [];

  if (mediaKind === 'image' && imageMediaAssets.length > 0) {
    const uploadProgress = new Array(imageMediaAssets.length).fill(0);

    for (const [index, imageAsset] of imageMediaAssets.entries()) {
      const preparedUpload =
        preparedMediaUploads?.[index] ??
        (imageMediaAssets.length === 1 ? preparedMediaUpload : null);
      const upload =
        preparedUpload?.kind === 'image'
          ? preparedUpload
          : await preparePostMediaUpload(imageAsset, 'image');
      const itemId = getPostMediaItemId(index);
      const storagePath = `posts/${authorUid}/${postRef.id}/${getPostMediaItemFileName(
        {
          fileName: upload.fileName,
          itemId,
        },
      )}`;
      const metadata: UploadMetadata = { contentType: upload.contentType };
      const storageRef = ref(storage, storagePath);

      const uploadSnapshot = await uploadBlobWithProgress({
        blob: upload.blob,
        metadata,
        onUploadProgress: onUploadProgress
          ? (itemProgress) => {
              uploadProgress[index] = itemProgress;
              const totalProgress =
                uploadProgress.reduce((sum, progress) => sum + progress, 0) /
                imageMediaAssets.length;
              onUploadProgress(totalProgress);
            }
          : undefined,
        storageRef,
      });

      mediaItems.push({
        id: itemId,
        order: index,
        kind: 'image',
        storagePath,
        downloadURL: await getDownloadURL(uploadSnapshot.ref),
        contentType: upload.contentType,
        sizeBytes: upload.sizeBytes,
        width: upload.width,
        height: upload.height,
        durationMillis: null,
        ...buildThumbnailFields('image'),
      });
    }

    media =
      mediaItems.length > 0 ? legacyPostMediaFromItem(mediaItems[0]) : null;
  } else if (mediaAsset && mediaKind) {
    const upload =
      preparedMediaUpload?.kind === mediaKind
        ? preparedMediaUpload
        : await preparePostMediaUpload(mediaAsset, mediaKind);
    const storagePath = `posts/${authorUid}/${postRef.id}/${upload.fileName}`;
    const metadata: UploadMetadata = { contentType: upload.contentType };
    const storageRef = ref(storage, storagePath);

    const uploadSnapshot = await uploadBlobWithProgress({
      blob: upload.blob,
      metadata,
      onUploadProgress,
      storageRef,
    });

    media = {
      kind: mediaKind,
      storagePath,
      downloadURL: await getDownloadURL(uploadSnapshot.ref),
      contentType: upload.contentType,
      sizeBytes: upload.sizeBytes,
      width: upload.width,
      height: upload.height,
      durationMillis: upload.durationMillis,
      ...buildThumbnailFields(mediaKind),
    };
  }

  const postData = {
    authorUid,
    authorFullName,
    body: trimmedBody || null,
    visibility,
    media,
    mediaItems,
    linkCard: null,
    ...(linkPreviewSourceUrl ? { linkPreviewSourceUrl } : {}),
    reshare,
    profileMentions: sanitizedMentions,
    likeCount: 0,
    reactionCounts: emptyReactionCounts(),
    reactionCount: 0,
    commentCount: 0,
    shareCount: 0,
    commentMutation: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const saveDiagnostics = postCreateSaveDiagnosticSummary({
    authorFullName: postData.authorFullName,
    authorUid: postData.authorUid,
    body: postData.body,
    linkPreviewSourceUrl: postData.linkPreviewSourceUrl,
    media,
    postKeys: Object.keys(postData),
    profileMentions: sanitizedMentions,
    reactionCountKeys: Object.keys(postData.reactionCounts),
    reshare,
    visibility,
  });

  try {
    await setDoc(postRef, postData);
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      try {
        await createPostCallable({
          body: trimmedBody,
          linkPreviewSourceUrl: linkPreviewSourceUrl ?? null,
          media,
          mediaItems,
          postId: postRef.id,
          profileMentions: sanitizedMentions,
          reshare,
          visibility,
        });

        return postRef.id;
      } catch (fallbackError) {
        if (debugDiagnostics) {
          throw new UserFacingError(
            `Firestore rejected the final post save and the server fallback failed. ${saveDiagnostics}. Fallback error: ${
              fallbackError instanceof Error
                ? fallbackError.message
                : String(fallbackError)
            }`,
          );
        }

        throw fallbackError;
      }
    }

    if (debugDiagnostics) {
      throw new UserFacingError(
        `Firestore rejected the final post save. ${saveDiagnostics}`,
      );
    }

    throw error;
  }

  return postRef.id;
}
